import React, { useEffect, useState } from 'react';
import { OuigooLocationPicker } from '@ouigoo/location-picker/react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const LocationPicker = () => {
  const [isCheckingEditability, setIsCheckingEditability] = useState(true);
  const [editabilityError, setEditabilityError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if address is editable on component mount
  useEffect(() => {
    const checkEditability = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const orderId = params.get('id');
        const access = params.get('access');

        if (!orderId || !access) {
          setEditabilityError("Paramètres manquants dans l'URL (id ou access)");
          setIsCheckingEditability(false);
          return;
        }

        const checkUrl = `${process.env.REACT_APP_BUSINESS_API_ENDPOINT}/v1/order/address_set/check?order_id=${encodeURIComponent(orderId)}&access=${encodeURIComponent(access)}`;
        const res = await fetch(checkUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.error('GET address_set/check failed', res.status, text);
          setEditabilityError("Impossible de vérifier l'état de la commande");
          setIsCheckingEditability(false);
          return;
        }

        const data = await res.json();
        if (!data.editable) {
          if (data.redirectTo) {
            window.location.href = data.redirectTo;
            return;
          }
          setEditabilityError("Cette commande ne permet plus la modification d'adresse");
          setIsCheckingEditability(false);
          return;
        }

        setIsCheckingEditability(false);
      } catch (e) {
        console.error('Editability check error', e);
        setEditabilityError("Erreur réseau lors de la vérification");
        setIsCheckingEditability(false);
      }
    };

    checkEditability();
  }, []);

  const submitAddress = async (result) => {
    try {
      setIsSubmitting(true);
      const params = new URLSearchParams(window.location.search);
      const orderId = params.get('id');
      const access = params.get('access');

      if (!orderId || !access) {
        alert("Paramètres manquants dans l'URL (id ou access)");
        setIsSubmitting(false);
        return;
      }

      const apiUrl = `${process.env.REACT_APP_BUSINESS_API_ENDPOINT}/v1/order/address_set?order_id=${encodeURIComponent(orderId)}&access=${encodeURIComponent(access)}`;
      const payload = {
        address: result?.address || result?.name || '',
        lat: result?.location?.lat ?? null,
        lng: result?.location?.lng ?? null,
      };

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('POST address_set failed', res.status, text);
        alert("Échec de l'enregistrement de l'adresse. Veuillez réessayer.");
        setIsSubmitting(false);
        return;
      }

      let redirectUrl = `/thank-you?${params.toString()}`;
      try {
        const data = await res.json();
        if (data && data.redirectTo) {
          redirectUrl = data.redirectTo;
        }
      } catch {}
      window.location.href = redirectUrl;
    } catch (e) {
      console.error('POST error', e);
      alert("Erreur réseau. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationSelect = (result) => {
    if (isSubmitting) return;
    submitAddress(result);
  };

  if (isCheckingEditability) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de la commande...</p>
        </div>
      </div>
    );
  }

  if (editabilityError) {
    return (
      <div className="h-screen flex items-center justify-center bg-white p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Modification impossible</h3>
          <p className="text-gray-600 mb-6">{editabilityError}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-10 py-3 bg-primary-custom text-white rounded-lg font-medium hover:bg-primary-custom-dark transition-colors duration-200"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      height: '100vh', 
      height: '100dvh', // Dynamic viewport height for mobile browsers
      width: '100vw', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'fixed',
      top: 0,
      left: 0,
      overflow: 'hidden'
    }}>
      <OuigooLocationPicker
        apiKey={GOOGLE_MAPS_API_KEY}
        theme="ouigoo"
        language="fr"
        centerOnUserLocation
        enableSatelliteView
        enableMyLocation
        enableSearch
        confirmButtonStyle="circular"
        onLocationSelect={handleLocationSelect}
        onError={(err) => console.error('LocationPicker error', err)}
        style={{ flex: 1, width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default LocationPicker;
